import React from 'react';

import axios from 'axios';
import dayjs from 'dayjs';
import Cookie from 'js-cookie';
import {
  isEmpty,
  forOwn,
  get,
  omitBy,
  isNil,
  debounce,
  assign,
  isUndefined,
  mapValues,
} from 'lodash';
import moment from 'moment';
import uploadcare from 'uploadcare-widget';
import { req } from 'react-reqq-lite';
import * as XLSX from 'xlsx';
import { useHistory } from 'react-router-dom';

import { toastError, toastWarning } from './toast';

const padBounds = (bounds, padding = 0.2) => {
  const x1 = bounds[0];
  const y1 = bounds[1];
  const x2 = bounds[2];
  const y2 = bounds[3];

  // const x_center = (((x1 + x2) - x1) / 2) + x1;
  // const y_center = (((y1 + y2) - y1) / 2) + y1;
  // const w_padded = (x2 - x1) * padding;
  // const h_padded = (y2 - y1) * padding;
  // const size = w_padded > h_padded ? w_padded : h_padded;

  // const new_x1 = Math.floor(x_center - (size / 2));
  // const new_y1 = Math.floor(y_center - (size / 2));
  // const new_x2 = Math.floor(x_center + (size / 2)) - new_x1;
  // const new_y2 = Math.floor(y_center + (size / 2)) - new_y1;

  const new_x1 = Math.floor(x1 - x2 * padding);
  const new_y1 = Math.floor(y1 - y2 * padding);
  const new_x2 = Math.floor(x2 + x2 * padding * 2);
  const new_y2 = Math.floor(y2 + y2 * padding * 2);

  return {
    x: new_x1,
    y: new_y1,
    w: new_x2,
    h: new_y2,
  };
};

export const autoCropFace = async (ucare_url) => {
  try {
    const res = await axios.get(`${ucare_url}detect_faces/`);
    const face = get(res, 'data.faces.0') || [];
    if (isEmpty(face)) {
      toastWarning('Unable to crop automatically. No face detected.');
      return ucare_url;
    }

    const { x, y, w, h } = padBounds(face);
    return `${ucare_url}-/crop/${w}x${h}/${x},${y}/-/preview/`;
  } catch (err) {
    toastWarning('Unable to crop automatically. Crop error!');
    return ucare_url;
  }
};

export const getDueDaysMoment = (date, time) =>
  `Due ${moment(
    `${moment(date, 'MMMM D, YYYY').format('MMMM D, YYYY')} ${time}`,
    'MMMM D, YYYY hh:mm a'
  ).fromNow()}`;

export const objectToUpperCase = (params) => {
  const newObjt = {};
  Object.keys(params).map((item) => {
    const x = '';
    newObjt[item] =
      params[item] && typeof params[item] === 'string'
        ? `${params[item].toUpperCase()}`
        : params[item];
    return x;
  });
  return newObjt;
};

export const timeSince = (from, to) => {
  const date = from.toDate();
  const date2 = to.toDate();

  const seconds = Math.floor((date2 - date) / 1000);

  let interval = seconds / 31536000;

  if (interval > 1) {
    return `${Math.floor(interval)} years`;
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return `${Math.floor(interval)} months`;
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return `${Math.floor(interval)} days`;
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return `${Math.floor(interval)} hours`;
  }
  interval = seconds / 60;
  if (interval > 1) {
    return `${Math.floor(interval)} minutes`;
  }
  return `${Math.floor(seconds)} seconds`;
};

export const stringToUpperCase = (string) => {
  if (!isNil(string) && typeof string === 'string') {
    return string.toUpperCase();
  }
  return '';
};

export const jsUcFirst = (string, replacement = '') => {
  if (string) {
    const x = (string || '')
      .toLowerCase()
      .replace(new RegExp('(?:\\b|_)([a-z])', 'g'), (e) => e.toUpperCase());
    return x;
  }
  return replacement;
};

export const jsUcOnlyFirst = (string, replacement = '') => {
  if (!string) return replacement;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const parseNumber = (str, default_value = false) => {
  const v = parseFloat(`${str}`.replace(/,/g, ''));
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(v)) return typeof default_value !== 'boolean' ? default_value : str;
  return v;
};

export const formatNumber = (v, decimal = 2) => {
  try {
    const n = parseNumber(v);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(n)) return v;
    return n.toLocaleString(undefined, {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    });
  } catch (err) {
    return v;
  }
};

export const formatDate = (date, format = 'mm/dd/yyyy', defaultValue = '-') => {
  if (!date) return defaultValue;
  const d = new Date(date);
  if (d.toString() === 'Invalid Date') return defaultValue;
  return dayjs(d).format(format);
};

export const replaceSomeString = (str, replacement = '*', replaceCount = 8) => {
  const strLength = str.length;
  const toReplace = str.slice(0, replaceCount - 1);

  const cvString = toReplace
    .split('')
    .map((i) => i.replace(i, replacement))
    .join('');

  const uvString = str.slice(toReplace.length, strLength);

  return cvString + uvString;
};

export const transformIncluded = (x, included) => {
  if (!included || isEmpty(included)) return x;
  const rowIncluded = {};
  forOwn(x.relationships, (v, k) => {
    rowIncluded[k] = Array.isArray(v.data)
      ? v.data.map(
          (z) =>
            included.find(
              (y) => y.type === get(z, 'type') && y.id === get(z, 'id')
            ) || {}
        )
      : included.find(
          (y) => y.type === get(v, 'data.type') && y.id === get(v, 'data.id')
        ) || {};
  });
  const { links, relationships, type, ...rest } = x;
  return { ...rest, included: rowIncluded };
};

export const transformIncludedDeep = (x, included) => {
  if (!included || isEmpty(included)) return x;
  const rowIncluded = {};
  forOwn(x.relationships, (v, k) => {
    rowIncluded[k] = Array.isArray(v.data)
      ? v.data.map((z) =>
          transformIncludedDeep(
            included.find(
              (y) => y.type === get(z, 'type') && y.id === get(z, 'id')
            ) || {},
            included
          )
        )
      : transformIncludedDeep(
          included.find(
            (y) => y.type === get(v, 'data.type') && y.id === get(v, 'data.id')
          ) || {},
          included
        );
  });
  const { links, relationships, type, ...rest } = x;
  return { ...rest, included: rowIncluded };
};

export const storage = {
  get: (key, defaultValue = false) => {
    try {
      return JSON.parse(sessionStorage.getItem(key)) || defaultValue;
    } catch (err) {
      return defaultValue;
    }
  },
  set: (key, value) => {
    const newValue = JSON.stringify(value);
    sessionStorage.setItem(key, newValue);
  },
  remove: (key) => {
    sessionStorage.removeItem(key);
  },
  clear: () => {
    sessionStorage.clear();
  },
};

export const removePersistState = (key) => {
  storage.remove(key);
};

export const replaceNullToString = (obj) => {
  const x = mapValues(obj, (v) => (v === null || v === undefined ? '' : v));
  return x;
};

export const removeNull = (obj) =>
  omitBy(obj, (x) => typeof x === 'undefined' || x === null);

export const removeEmpty = (obj) =>
  omitBy(obj, (x) => isEmpty(`${x}`) || isUndefined(`${x}`));

export const getEllipsis = (str, count = 120) =>
  str.length > count ? `${str.substring(0, count)}...` : str;

export const generateArrDate = (date_from, date_to, type) => {
  const renderType = {
    day: 'll',
    month: 'MMM, YYYY',
    year: 'YYYY',
  };

  const dates = [];
  const dateRange = dayjs(date_to).diff(dayjs(date_from), type);
  for (let i = 0; i <= dateRange; i += 1) {
    dates.push(dayjs(date_from).add(i, type).format(renderType[type]));
  }
  return dates;
};

export const getFirstMessage = (data) => {
  if (!data) return '';
  let firstMessage = '';
  let x = 0;

  Object.keys(data).map((i) => {
    if (x === 0) {
      firstMessage =
        get(data[i], 'details') ||
        get(data[i], 'detail') ||
        get(data[i], 'message') ||
        get(data[i], '0');
    }
    x += 1;
    return x;
  });

  return jsUcOnlyFirst(firstMessage);
};

export const usePersistState = (key, state, isClearable = true) => {
  const [value, setValue] = React.useState(storage.get(key) || state);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateState = React.useCallback(
    debounce((newState) => {
      storage.set(key, newState);
    }, 200),
    []
  );

  React.useEffect(() => {
    updateState(value);
  }, [key, updateState, value]);

  React.useEffect(
    () => () => {
      if (isClearable) {
        storage.remove(key);
      }
    },
    [isClearable, key]
  );

  return [value, setValue];
};

export const queryParams = (params, prefix) => {
  const query = Object.keys(params).map((key) => {
    let k = key;
    const value = params[key];

    if (params.constructor === Array) {
      k = `${prefix}[]`;
    }
    if (params.constructor === Object) {
      k = prefix ? `${prefix}[${key}]` : key;
    }

    if (typeof value === 'object') return queryParams(value, k);
    return `${key}=${encodeURIComponent(value)}`;
  });

  return [].concat([], ...query).join('&');
};

export const downloadGet = ({
  url,
  filename,
  params = null,
  onSuccess = () => {},
  isImage = false,
}) => {
  try {
    const token = Cookie.get('_token');
    const $url = params
      ? `${process.env.REACT_APP_END_POINT}${url}?${queryParams(params)}`
      : `${process.env.REACT_APP_END_POINT}${url}`;

    let dispatchFetch = fetch($url, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${token}`,
      }),
    });

    if (isImage) {
      dispatchFetch = fetch(url);
    }

    dispatchFetch
      .then((response) => response.blob())
      .then((blob) => {
        const temp_url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = temp_url;
        a.download = `${filename}` || 'filename.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        onSuccess();
      })
      .catch((err) => {
        console.log(err); // eslint-disable-line
        toastError('Unable to process request!');
      });
  } catch (err) {
    toastError('Unable to process request!');
  }
};

export const convertArrayToObject = (array, key) =>
  array.reduce((acc, curr) => {
    acc[curr[key]] = curr;
    return acc;
  }, {});

export const flatRecursiveArray = (array, key = 'child') =>
  array.reduce((acc, curr) => {
    const child = curr?.[key];

    if (Array.isArray(child)) {
      return acc.concat([curr, ...child]);
    }
    return acc.concat(curr);
  }, []);

export const recursiveFlatArray = (
  array,
  target_id = 'id',
  link = 'parent_id',
  child_key = 'child',
  id = null
) => {
  array?.filter((x) => get(x, link) === id);
  return array
    ?.filter((x) => get(x, link) === id)
    .map((x) => ({
      ...x,
      [child_key]: recursiveFlatArray(
        array,
        `${target_id}`,
        `${link}`,
        `${child_key}`,
        get(x, target_id)
      ),
    }));
};

export const replaceAllString = (str, target, replacement = '') => {
  if (!str) return '';
  const x = str?.replaceAll(target, replacement);
  return x;
};

export const insertUpdateObjectToList = (list = [], data = {}, id = 'id') => {
  const newList = list.map((item) => {
    if (`${data[id]}` !== `${item[id]}`) return item;
    return assign({}, item, data);
  });

  return newList;
};

export const insertNewObjectToList = (
  list = [],
  data = {},
  position = 'end'
) => {
  if (position === 'start') {
    return [data].concat(list);
  }
  return list.concat([data]);
};

export const removeObjectToList = (list = [], id, target_id = 'id') => {
  let newList = [];
  newList = list.filter((item) => `${item[target_id]}` !== `${id}`);
  return newList;
};

export const formatedSelectOption = (
  list = [],
  value = 'id',
  label = 'name',
  { isLabelUpperCase = true, isValueUpperCase = false, withData = false } = {}
) => {
  const newData = [];

  list.forEach((item) => {
    const args = {
      value: isValueUpperCase
        ? get(item, `${value}`).toUpperCase()
        : get(item, `${value}`),
      label: isLabelUpperCase
        ? get(item, `${label}`).toUpperCase()
        : get(item, `${label}`),
    };
    newData.push(
      !withData
        ? args
        : {
            ...args,
            data: item,
          }
    );
  });
  return newData;
};

export const groupAndSumArray = (list, groupKeys, sumKeys) =>
  Object.values(
    list.reduce((acc, curr) => {
      const group = groupKeys.map((k) => curr[k]).join('-');
      acc[group] = {
        ...{
          ...(acc[group] ||
            Object.fromEntries(
              groupKeys
                .map((k) => [k, curr[k]])
                .concat(sumKeys.map((k) => [k, 0]))
            )),
        },
        data: curr,
      };

      // eslint-disable-next-line no-return-assign
      sumKeys.forEach((k) => (acc[group][k] += curr[k]));
      return acc;
    }, {})
  );

export const objectToString = (obj) => {
  try {
    return JSON.stringify(obj);
  } catch (error) {
    return '';
  }
};

export const stringToObject = (str, defaultValue = {}) => {
  try {
    return JSON.parse(str);
  } catch (error) {
    return defaultValue;
  }
};

export const formatGetQueryParams = ($data, enableEmpty) => {
  let data = $data;
  if (enableEmpty) data = removeNull(removeEmpty($data));

  const response = Object.entries(data).reduce((accumulator, current) => {
    if (Array.isArray(current[1])) {
      const abc = current[1].reduce((a, b, i) => {
        if (typeof b === 'string' || typeof b === 'number') {
          return {
            ...a,
            [`${current[0]}[${i}]`]: b,
          };
        }
        return {
          ...a,
          [`${current[0]}[${i}]`]: b.value,
        };
      }, {});

      return {
        ...accumulator,
        ...abc,
      };
    }

    if (typeof current[1] === 'object' && current[1]?.label) {
      return {
        ...accumulator,
        [current[0]]: current[1]?.value,
      };
    }
    if (
      typeof current[1]?.value === 'boolean' ||
      typeof current[1]?.value === 'number'
    ) {
      return {
        ...accumulator,
        [current[0]]: current[1]?.value,
      };
    }

    return {
      ...accumulator,
      [current[0]]: current[1],
    };
  }, {});
  return response;
};

export const formatMobileNumber = (val) => {
  const regEx = /[()+\s]/g;
  const x = replaceAllString(val, regEx);
  return x;
};

export const formatStringToUrl = (url = '') => {
  if (!url?.includes('https://') && url) return `https://${url}`;
  return url;
};

export const formatCurrency = (number, currency = 'PHP', config = {}) =>
  Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    ...config,
  }).format(number);

/* eslint-disable */
export const cartesian = (...args) => {
  try {
    var r = [],
      max = args.length - 1;
    function helper(arr, i) {
      for (var j = 0, l = args[i].length; j < l; j++) {
        var a = arr.slice(0); // clone arr
        a.push(args[i][j]);
        if (i == max) r.push(a);
        else helper(a, i + 1);
      }
    }
    helper([], 0);
    return r;
  } catch (err) {
    return [];
  }
};
/* eslint-enable */

export const generateGreeting = () => {
  const hour = dayjs().hour();

  if (hour > 16) {
    return 'Good Evening';
  }

  if (hour > 11) {
    return 'Good Afternoon';
  }

  return 'Good Morning';
};

export const focusElement = (id) => {
  try {
    const elem = document.getElementById(id);
    if (elem) elem.focus();
  } catch (err) {
    // unable to select
  }
};

export const uploadPhotos = (files, options) =>
  new Promise((resolve) => {
    const opt = {
      // tabs: ['file', 'url', 'camera'],
      // crop: '1:1',
      publicKey: process.env.REACT_APP_UPLOADCARE_KEY,
      transform: (x) => x,
      ...options,
    };
    Promise.all(
      files.map(
        (file) =>
          new Promise((r) => {
            uploadcare.fileFrom('object', file, opt).done(r);
          })
      )
    ).then((out) => {
      resolve(out.map(opt.transform));
    });
  });

export const filesToArr = (raw) => {
  const f = [];
  for (let i = 0; i < raw.length; i += 1) {
    f.push(raw[i]);
  }
  return f;
};

export const print = (el, { style = '', callback = () => {} } = {}) => {
  try {
    const mywindow = window?.open('print', 'PRINT');

    mywindow?.document?.write(`<html><head>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">

    <style>
      @page { size: auto; margin: 0mm 20px; }

      * {
        font-family: Inter, Arial, sans-serif;
        font-size: 10px;
      }
      
      ${style}
      </style>
  </head>
  <body>`);
    mywindow?.document?.write(document.getElementById(el).innerHTML);
    mywindow?.document?.write('</body></html>');

    mywindow?.document?.close(); // necessary for IE >= 10
    mywindow?.focus(); // necessary for IE >= 10*/

    setTimeout(() => {
      mywindow.print();
      mywindow.close();
      callback();
    }, 250);
  } catch (e) {
    toastError('Something went wrong, please try again.');
  }
};

export const postFormData = (url, config) => {
  const { method, onProgress, onSuccess, formData, key } = config || {};
  const loaderKey = `LOADING/${(method || '')?.toLowerCase()}/${key}`;

  req.set(loaderKey, true);

  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.timeout = 300000;
      xhr.addEventListener('timeout', () => {
        reject(
          new Error({
            message: 'Request Timeout, Please try again.',
          })
        );
      });
      xhr.open(method, url);
      const token = Cookie.get('_token');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.onload = () => {
        try {
          const jsonResponse = JSON.parse(xhr.response || '');

          if (xhr.status !== 200) {
            req.set(loaderKey, false);
            return reject(jsonResponse);
          }

          req.set(loaderKey, false);
          if (typeof onSuccess === 'function') onSuccess(jsonResponse);
          return resolve(jsonResponse);
        } catch (err) {
          req.set(loaderKey, false);
          reject(err);
          return null;
        }
      };
      xhr.onerror = () => {
        const errRes = {
          message: 'Oops..Something went wrong.',
        };
        req.set(loaderKey, false);
        return reject(errRes);
      };

      if (xhr.upload) {
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            const progress = Math.ceil((evt.loaded / evt.total) * 100);
            if (onProgress) onProgress(progress);
          }
        };
      }

      xhr.send(formData);
    } catch (err) {
      req.set(loaderKey, false);
      reject(err);
    }
  });
};

export const parseExcel = async (file) => {
  const data = await file?.arrayBuffer();
  const wb = XLSX.readFile(data);

  const ws = wb.Sheets[wb.SheetNames[0]];

  const $data = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: '',
  });

  const headers = $data?.[0];
  $data?.shift();

  const result = $data?.reduce(
    (acc, item, key) => [
      ...acc,
      headers?.reduce(
        (subAcc, subItem, subKey) => ({
          ...subAcc,
          [subItem]: $data?.[key]?.[subKey],
        }),
        {}
      ),
    ],
    []
  );

  return result;
};

export function isJson(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export function useNavigate() {
  const history = useHistory();

  const goBack = (index = -1, path = '') => {
    const currentPath = path || window.location.pathname;

    const previousPath = (currentPath || '')
      ?.split('/')
      ?.slice(0, index)
      ?.join('/');

    history.push(previousPath);
  };

  return goBack;
}
